<template>
    <UFieldGroup class="w-full">
        <UTooltip :text="showError? 'Velg eller opprett produkt' : ''">
            <USelectMenu
                v-model="product"
                data-cy="product-picker"
                :class="[product ? 'w-fit': 'w-full']"
                :items="activeProducts"
                label-key="name"
                size="sm"
                create-item
                :ui="{ content: 'min-w-fit '+theme}"
                :color="showError ? 'error': 'neutral'"
                :highlight="showError"
                @create="onCreate"
                @update:model-value:product="onProductSelected"
                @blur="touched = true"
            >
                <span
                    v-if="product"
                    class="text-primary px-1 rounded border-primary whitespace-nowrap w-16 truncate"
                    :title="product.code"
                >
                    {{ product.code }}
                </span>
                <template #item="{item}">
                    <div class="flex items-baseline w-full gap-2" :title="item.code" :data-cy="item.code" >
                        <div class="text-highlighted w-16 max-w-16 truncate font-semibold">
                            {{ item.code }}
                        </div>
                        <div class="grow truncate flex flex-col">
                            <div class="truncate">{{ item.name }}</div>
                            <div class="text-muted truncate">{{ item.description }}</div>
                        </div>
                        <div class="w-16 min-w-16 text-right text-highlighted">
                            {{ formatCurrency(item.retailPrice) }}
                        </div>
                    </div>
                </template>
                <template #content-bottom>
                    <UButton
                        size="sm"
                        color="neutral"
                        variant="outline"
                        class="w-fit m-1"
                        icon="i-ph-plus-circle"
                        @click="onCreate('')"
                    >
                        Nytt produkt
                    </UButton>
                </template>
            </USelectMenu>
        </UTooltip>
        <UInput
            v-if="product"
            v-model="description"
            size="sm"
            class="w-full"
            autofocus
        >
            <template v-if="product" #trailing>
                <UButton
                    color="error"
                    variant="ghost"
                    size="sm"
                    icon="i-ph-x"
                    title="Fjern produkt"
                    @click="clearProduct"
                />
            </template>
        </UInput>
    </UFieldGroup>
</template>

<script setup lang="ts">
import {useProducts} from '@/products/product.service'
import type {Product} from '@/products/product.types'
import {formatCurrency} from '@/services/formatter.service'
import ProductEditorPopup from '@/products/ProductEditorPopup.vue'
import {computed, ref} from 'vue'
import {useOverlay} from '@nuxt/ui/composables/useOverlay'

const emit = defineEmits<{
    (e: 'product-created', product: Product): void
}>()

const overlay = useOverlay()
const modal = overlay.create(ProductEditorPopup)

const touched = ref(false)

const {products, getDefaultNewProduct} = useProducts()

const activeProducts = computed<Product[]>(() => {
    return products.value.filter((product: Product) => product.active)
})

const product = defineModel<Product>('product')
const description = defineModel<string>('description', {default: ''})

defineProps({
    theme: {type: String, default: ''}  //TODO: Type to available theme classes?
})

const onCreate = (name: string) => {
    const newProduct: Product = {
        ...getDefaultNewProduct(),
        code: name,
        name
    }
    modal.open({product: newProduct})
        .then((savedProduct: Product | undefined) => {
            if (savedProduct) {
                onProductSave(savedProduct)
            }
        })
}

const onProductSave = (savedProduct: Product) => {
    product.value = savedProduct
    description.value = savedProduct.name
    emit('product-created', savedProduct)
}

const onProductSelected = (selectedProduct: Product) => {
    if (selectedProduct) {
        product.value = selectedProduct
        description.value = selectedProduct.name
    } else {
        description.value = ''
    }
}

const clearProduct = () => {
    product.value = undefined
}

const showError = computed(() => {
    return touched.value && !product.value
})

</script>

<style scoped>

</style>